import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as signalR from '@microsoft/signalr';
import { createMatchHubConnection, MatchLiveState, MatchEndedPayload } from '../../utils/matchHub';
import { MatchClient, Match } from '../../utils/apiClient';
import ApiUrl from '../../utils/config';
import './MatDisplay.scss';

// Публичная страница табло: открывается один раз на мониторе над конкретным ковром
// и весь день показывает то, что происходит именно на нём — независимо от того,
// какая пара сейчас борется. Судейская панель (MatchConduct) транслирует сюда события
// через MatchHub; здесь нет ни логики правил, ни запросов на запись — только отображение.
//
// URL: /display/1              — только живой счёт, без сетки в перерывах (не знаем турнир)
// URL: /display/1?bracketId=12 — в перерывах показывает ближайшие поединки этой сетки

type Mode = 'idle' | 'live' | 'ended';

const formatTime = (ms: number) => {
  const clamped = Math.max(0, ms);
  const minutes = Math.floor(clamped / 60000);
  const seconds = Math.floor((clamped % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const getParticipantName = (m: Match, isHome: boolean) => {
  const participant = isHome ? m.homeParticipant : m.awayParticipant;
  return participant?.person
    ? `${participant.person.lastName || ''} ${participant.person.firstName || ''}`.trim()
    : 'TBD';
};

const MatDisplay: React.FC = () => {
  const { matNumber } = useParams<{ matNumber: string }>();
  const [searchParams] = useSearchParams();
  const bracketId = searchParams.get('bracketId');

  const [mode, setMode] = useState<Mode>('idle');
  const [liveState, setLiveState] = useState<MatchLiveState | null>(null);
  const [endedInfo, setEndedInfo] = useState<MatchEndedPayload | null>(null);
  const [upcoming, setUpcoming] = useState<Match[]>([]);
  const [roundLabel, setRoundLabel] = useState('Раунд');
  const [connectionState, setConnectionState] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Connecting
  );

  // Тик для локального отсчёта таймера — не зависим от сети, просто пересчитываем от timerAnchorUtc.
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const endedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUpcoming = React.useCallback(async () => {
    if (!bracketId) return;
    try {
      const matchClient = new MatchClient(ApiUrl, axios);
      const [matches, bracketResp] = await Promise.all([
        matchClient.getMatches(Number(bracketId)),
        axios.get(`${ApiUrl}/api/Bracket/GetBracket?id=${bracketId}`).catch(() => null),
      ]);
      const format = bracketResp?.data?.format as string | undefined;
      setRoundLabel(format === 'RoundRobin' ? 'Тур' : 'Раунд');
      const notFinished = matches
        .filter(m => !m.endTime)
        .sort((a, b) => (a.round || 0) - (b.round || 0) || (a.order || 0) - (b.order || 0));
      setUpcoming(notFinished.slice(0, 10));
    } catch {
      setUpcoming([]);
    }
  }, [bracketId]);

  useEffect(() => {
    if (!matNumber) return;

    const connection = createMatchHubConnection();

    connection.on('MatchStateUpdated', (state: MatchLiveState) => {
      if (endedTimeoutRef.current) {
        clearTimeout(endedTimeoutRef.current);
        endedTimeoutRef.current = null;
      }
      setLiveState(state);
      setEndedInfo(null);
      setMode('live');
    });

    connection.on('MatchEnded', (payload: MatchEndedPayload) => {
      // Победитель остаётся на табло до тех пор, пока ковёр не займёт следующий поединок
      // (событие MatchStateUpdated) или судья явно не отправит MatIdle — сами по таймеру
      // на сетку/расписание не уходим.
      setEndedInfo(payload);
      setMode('ended');
    });

    connection.on('MatIdle', () => {
      if (endedTimeoutRef.current) {
        clearTimeout(endedTimeoutRef.current);
        endedTimeoutRef.current = null;
      }
      setMode('idle');
      setLiveState(null);
      fetchUpcoming();
    });

    const join = () => {
      connection.invoke('JoinMat', matNumber)
        .then(() => connection.invoke('RequestState', matNumber))
        .catch(err => console.error('[табло] не удалось подписаться на ковёр', err));
    };

    connection.onreconnecting(() => setConnectionState(signalR.HubConnectionState.Reconnecting));
    connection.onreconnected(() => {
      setConnectionState(signalR.HubConnectionState.Connected);
      join();
    });
    connection.onclose(() => setConnectionState(signalR.HubConnectionState.Disconnected));

    connection.start()
      .then(() => {
        setConnectionState(signalR.HubConnectionState.Connected);
        join();
      })
      .catch(err => {
        console.error('[табло] не удалось подключиться к хабу', err);
        setConnectionState(signalR.HubConnectionState.Disconnected);
      });

    fetchUpcoming();

    return () => {
      if (endedTimeoutRef.current) clearTimeout(endedTimeoutRef.current);
      connection.stop();
    };
  }, [matNumber, fetchUpcoming]);

  const remainingMs = useMemo(() => {
    if (!liveState) return 0;
    if (!liveState.isTimerRunning || !liveState.timerAnchorUtc) {
      return liveState.timeRemainingMsAtAnchor;
    }
    const anchor = new Date(liveState.timerAnchorUtc).getTime();
    const elapsed = tick - anchor;
    return Math.max(0, liveState.timeRemainingMsAtAnchor - elapsed);
  }, [liveState, tick]);

  const osaekomiSeconds = useMemo(() => {
    if (!liveState?.osaekomiSide || !liveState.osaekomiStartedUtc) return 0;
    const started = new Date(liveState.osaekomiStartedUtc).getTime();
    return Math.max(0, Math.floor((tick - started) / 1000));
  }, [liveState, tick]);

  return (
    <div className="mat-display">
      <div className="mat-display__mat-label">Ковёр №{matNumber}</div>

      {connectionState !== signalR.HubConnectionState.Connected && (
        <div className="mat-display__connection-warning">Нет связи с сервером...</div>
      )}

      {mode === 'live' && liveState && (
        <div className="mat-display__live">
          <div className={`mat-display__timer ${liveState.osaekomiSide ? 'osaekomi-active' : ''}`}>
            {formatTime(remainingMs)}
            {liveState.osaekomiSide && (
              <div className="mat-display__osaekomi">
                УДЕРЖАНИЕ ({liveState.osaekomiSide === 'home' ? liveState.homeName : liveState.awayName}): {osaekomiSeconds}с
              </div>
            )}
          </div>

          <div className="mat-display__participants">
            <div className="mat-display__participant">
              <div className="mat-display__name">{liveState.homeName}</div>
              <div className="mat-display__scores">
                <div><span>{liveState.homeIppon}</span><label>Иппон</label></div>
                <div><span>{liveState.homeWazaAri}</span><label>Вазари</label></div>
                <div><span>{liveState.homeYuko}</span><label>Юко</label></div>
                <div className="shido"><span>{liveState.homeShido}</span><label>Шидо</label></div>
              </div>
            </div>

            <div className="mat-display__vs">vs</div>

            <div className="mat-display__participant">
              <div className="mat-display__name">{liveState.awayName}</div>
              <div className="mat-display__scores">
                <div><span>{liveState.awayIppon}</span><label>Иппон</label></div>
                <div><span>{liveState.awayWazaAri}</span><label>Вазари</label></div>
                <div><span>{liveState.awayYuko}</span><label>Юко</label></div>
                <div className="shido"><span>{liveState.awayShido}</span><label>Шидо</label></div>
              </div>
            </div>
          </div>

          {!liveState.isStarted && <div className="mat-display__hint">Поединок скоро начнётся</div>}
          {liveState.isStarted && liveState.isPaused && <div className="mat-display__hint">Пауза</div>}
        </div>
      )}

      {mode === 'ended' && endedInfo && (
        <div className="mat-display__ended">
          <div className="mat-display__ended-label">Поединок завершён</div>
          {liveState && (
            <div className="mat-display__ended-pair">
              {liveState.homeName} — {liveState.awayName}
            </div>
          )}
          <div className="mat-display__ended-winner">
            {endedInfo.winnerName === 'Победитель' ? 'Победитель' : `Победитель ${endedInfo.winnerName}`}
          </div>
        </div>
      )}

      {mode === 'idle' && (
        <div className="mat-display__idle">
          {upcoming.length > 0 ? (
            <>
              <div className="mat-display__idle-title">Ближайшие поединки</div>
              <ul className="mat-display__upcoming">
                {upcoming.map(m => (
                  <li key={m.id}>
                    <span className="round">{roundLabel} {m.round}</span>
                    <span className="pair">{getParticipantName(m, true)} — {getParticipantName(m, false)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="mat-display__idle-title">Ожидание поединка</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatDisplay;
